import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GridComponent, FilterSettingsModel, IFilter, ToolbarItems, GroupSettingsModel, IFilterUI, TextWrapSettingsModel, RowDataBoundEventArgs } from '@syncfusion/ej2-angular-grids';
import { ClickEventArgs } from '@syncfusion/ej2-angular-navigations';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { DemoService } from 'src/app/demo.service';
import { GlobalFunctionsService } from 'src/app/services/global-functions.service';
import { HotToastService } from '@ngneat/hot-toast';
import { sortResolutionOutcomes } from 'src/app/shared/functions/subject-service-resolution-outcome-display';

@Component({
  selector: 'app-subject-service-resolution-outcome-type-selector',
  templateUrl: './subject-service-resolution-outcome-type-selector.component.html',
  styleUrls: ['./subject-service-resolution-outcome-type-selector.component.scss']
})
export class SubjectServiceResolutionOutcomeTypeSelectorComponent implements OnInit, OnChanges {
  @Input() selectedTypeId?: number;
  @Input() widget_size?: any;
  @Input() item_id?;
  @Input() item_data?;
  @Input() use_item_data? = 'no';
  @Input() item_user_level_id? = '5';
  @Input() item_brand_type_id? = '';
  @Input() show_buttons? = 'no';
  @Input() widget_title? = '';
  @Input() current_field_name?;

  @Output('closeParentModel') closeParentModel: EventEmitter<any> = new EventEmitter();
  @Output('closeParentModelDefault') closeParentModelDefault: EventEmitter<any> = new EventEmitter();
  @Output('addRecipientFromModal') addRecipientFromModal: EventEmitter<any> = new EventEmitter();
  @Output('addAutomationFromModal') addAutomationFromModal: EventEmitter<any> = new EventEmitter();
  @Output('addResolutionFromModal') addResolutionFromModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('grid') public grid: GridComponent;
  @ViewChild('modalContent') modalContent: TemplateRef<any>;

  resolution_outcomes: any[] = [];
  outcomeForm!: FormGroup;
  modalAction: 'create' | 'edit' | 'remove' = 'create';
  selectedId?: number;
  modalRef?: BsModalRef;

  public pageOptions = { pageSize: 100 };
  public filterOptions: FilterSettingsModel = { type: 'Menu' };
  public filter: IFilter;
  public toolbarOptions: ToolbarItems[] = ['Search'];
  public groupOptions: GroupSettingsModel;
  public templateOptionsDate: IFilterUI;
  public initialPage = { pageSize: 5 };
  public wrapSettings: TextWrapSettingsModel = { wrapMode: 'Content' };

  constructor(
    private demoService: DemoService,
    public toastr: HotToastService,
    public gfunc: GlobalFunctionsService,
    private modalService: BsModalService,
    private fb: FormBuilder,
  ) {}

  ngOnInit() {
    this.initForm();
    if (this.selectedTypeId) {
      this.getSubjectServiceResolutionOutcomesByType(this.selectedTypeId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedTypeId && this.selectedTypeId) {
      this.selectedTypeId = Number(this.selectedTypeId);
      this.getSubjectServiceResolutionOutcomesByType(this.selectedTypeId);
      this.initForm();
    }
  }

  initForm(): void {
    this.outcomeForm = this.fb.group({
      id: [''],
      name: ['', Validators.required],
      outcome: ['', Validators.required],
      score: [0],
      sort_order: [0],
      is_active: [1],
      is_visible: [1],
      colour: ['#35b684'],
      icon: ['fa fa-circle-thin'],
      subject_service_resolution_outcome_type_id: [this.selectedTypeId || '', Validators.required]
    });
  }

  getSubjectServiceResolutionOutcomesByType(typeId: number) {
    this.demoService.getSubjectServiceResolutionOutcomesByType(typeId)
      .subscribe(
        (res: any) => this.resolution_outcomes = sortResolutionOutcomes(res || []),
        err => console.error(err.status)
      );
  }

  openModal(action: 'create' | 'edit' | 'remove', id?: number): void {
    if (!this.selectedTypeId && action === 'create') {
      this.toastr.error('Please choose an outcome type first.');
      return;
    }

    this.modalAction = action;
    this.selectedId = id;
    this.initForm();
    this.outcomeForm.patchValue({
      subject_service_resolution_outcome_type_id: this.selectedTypeId
    });

    if ((action === 'edit' || action === 'remove') && id) {
      this.demoService.getSubjectServiceResolutionOutcome(id).subscribe(
        (data: any) => {
          const res = data && data[0] ? data[0] : null;
          if (res) {
            this.outcomeForm.patchValue({
              id: res.id,
              name: res.name,
              outcome: res.outcome,
              score: res.score,
              sort_order: res.sort_order,
              is_active: res.is_active,
              is_visible: res.is_visible == null || res.is_visible === '' ? 1 : res.is_visible,
              colour: res.colour,
              icon: res.icon,
              subject_service_resolution_outcome_type_id: res.subject_service_resolution_outcome_type_id
            });
          }
        },
        err => console.error(err.status)
      );
    }

    this.modalRef = this.modalService.show(this.modalContent);
  }

  saveOutcome(): void {
    if (!this.outcomeForm.valid) {
      return;
    }

    const outcomeData = {
      ...this.outcomeForm.value,
      is_active: this.outcomeForm.value.is_active ? 1 : 0,
      is_visible: this.outcomeForm.value.is_visible ? 1 : 0
    };

    if (this.modalAction === 'create') {
      this.demoService.createSubjectServiceResolutionOutcome(outcomeData).subscribe(
        () => this.afterOutcomeSaved('Resolution Outcome Added'),
        () => this.toastr.error('Adding Resolution Outcome Failed')
      );
    } else if (this.modalAction === 'edit' && this.selectedId) {
      this.demoService.updateSubjectServiceResolutionOutcome(this.selectedId, outcomeData).subscribe(
        () => this.afterOutcomeSaved('Resolution Outcome Updated'),
        () => this.toastr.error('Updating Resolution Outcome Failed')
      );
    }
  }

  removeOutcome(): void {
    if (!this.selectedId) {
      return;
    }

    this.demoService.deleteSubjectServiceResolutionOutcome(this.selectedId).subscribe(
      () => this.afterOutcomeSaved('Resolution Outcome Removed'),
      () => this.toastr.error('Removing Resolution Outcome Failed')
    );
  }

  afterOutcomeSaved(message: string): void {
    this.toastr.success(message);
    this.closeModal();
    if (this.selectedTypeId) {
      this.getSubjectServiceResolutionOutcomesByType(this.selectedTypeId);
    }
  }

  closeModal(): void {
    if (this.modalRef) {
      this.modalRef.hide();
    }
  }

  toolbarClick(args: ClickEventArgs): void {
    if (args.item.id === 'Grid_excelexport' && this.grid) {
      this.grid.excelExport();
    }
  }

  rowDataBound(args: RowDataBoundEventArgs) {
    const job_status = 'job_status';
    if (args.data[job_status] === 'CLOSED') {
      args.row.className = 'strikethrough';
    }
  }

  refreshGrid() {
    if (this.selectedTypeId) {
      this.getSubjectServiceResolutionOutcomesByType(this.selectedTypeId);
    }
    if (this.grid) {
      this.grid.refresh();
    }
  }
}
